import Button from '@/components/Button'
import IcoMoon from '@/components/IcoMoon'
import { Stack } from '@/components/Layout'
import Text from '@/components/Text'
import { coreLayoutContainer, coreLayoutSection, rbp } from '@/constants/measured-scope'
import { useAuth } from '@/context/auth'
import { getConfig } from '@/lib/config'
import { HeroInfoContentVideo, HeroInfoNavigationAction, UserStats } from '@/shared/api-generated-types'
import Modal from '@/widgets/Modal'
import PartnerInfo from '@/widgets/PartnerInfo'
import { useMediaQuery } from '@react-hookz/web'
import { useTranslation } from 'next-i18next'
import { useState } from 'react'
import * as styles from './style'

export interface Props {
  className?: string
  userStats?: UserStats
  partner?: {
    name?: string
    description?: string
    logo?: string
  }
  video?: HeroInfoContentVideo
  navigationActions?: HeroInfoNavigationAction[]
}

const HelenInfoBlock = (props: Props): JSX.Element => {
  const { t } = useTranslation('common')
  const { state: auth } = useAuth()
  const isMobile = useMediaQuery(`only screen and (max-width : ${rbp.sm})`, { initializeWithValue: false })
  const config = getConfig()

  const [partnerInfoPopup, setPartnerInfoPopup] = useState<boolean>(false)
  const togglePartnerInfoPopupClose = (): void => {
    setPartnerInfoPopup(!partnerInfoPopup)
  }

  const isLargeDevice = useMediaQuery(`only screen and (min-width : ${rbp.smallTablet})`, {
    initializeWithValue: false,
  })
  const iconSize = !isLargeDevice ? 10 : 14

  return (
    <header className={`${coreLayoutSection} ${styles.root}`}>
      {props.userStats ? (
        <Stack x className={`${coreLayoutContainer} flex !flex-col-reverse desktop:!flex-row !mb-5`}>
          <Stack y alignItems="start" spaceBetweenY="xs" className="mt-8">
            <Text
              weight="md"
              size="lg"
              responsiveSize={{
                lg: 'xl',
              }}
            >
              Welcome back <span className="font-bold">{auth.user?.firstName}</span>, what can I help you with today?
            </Text>
            <div className="flex flex-row justify-start w-full gap-2">
              <div className="flex gap-2">
                <Text className={styles.userStat} size="xs" responsiveSize={{ lg: 'md' }} weight="lg">
                  {props.userStats.formattedActiveDays}
                </Text>
                <Text size="xs" responsiveSize={{ lg: 'md' }} weight="sm">
                  {t('userStats.activeDays')}
                </Text>
              </div>
              <span className="font-bold fc-grey block">|</span>
              <div className="flex gap-2">
                <Text className={styles.userStat} size="xs" responsiveSize={{ lg: 'md' }} weight="lg">
                  {props.userStats.formattedTotalMinutes}
                </Text>
                <Text size="xs" responsiveSize={{ lg: 'md' }} weight="sm">
                  {t('userStats.wellnessMinutes')}
                </Text>
              </div>
            </div>
          </Stack>
          {props.partner?.logo && (
            <Stack className="w-full desktop:w-auto">
              <a
                className={`${styles.sponsoredText} flex flex-row desktop:flex-col-reverse items-center`}
                onClick={togglePartnerInfoPopupClose}
              >
                <Stack x justifyContent="flex-start" alignItems="center" className="mb-0 mb-xxs-gte-sm">
                  <IcoMoon icon="info" size={iconSize} className="mr-xxxs" />
                  <Text>Sponsored By</Text>
                </Stack>
                <div className={`${styles.logo} ${styles.logoMember}`}>
                  <img src={props.partner.logo} alt={props.partner.name ?? ''} data-testid="logo" loading="lazy" />
                </div>
              </a>
              {partnerInfoPopup ? (
                <Modal onDismiss={togglePartnerInfoPopupClose}>
                  <PartnerInfo
                    onClickCancel={togglePartnerInfoPopupClose}
                    description={props.partner.description}
                    logo={props.partner.logo}
                    name={props.partner.name}
                  />
                </Modal>
              ) : null}
            </Stack>
          )}
        </Stack>
      ) : props.partner?.logo ? (
        <Stack className={`${coreLayoutContainer} !mt-8`}>
          <div className="flex flex-col gap-4 md:gap-8">
            <Stack y justifyContent="center" spaceBetweenY="sm">
              <Text
                weight="lg"
                size="md"
                responsiveSize={{
                  lg: 'xl',
                }}
              >
                Stay Active, Engaged, and Connected with Free Classes from GetSetUp!
              </Text>
              <div className="flex flex-row items-center">
                <Text tag="p" size="xs" weight="sm" responsiveSize={{ md: 'sm' }}>
                  We have teamed up with GetSetUp to provide hundreds of live online classes to keep you mentally,
                  physically and socially active. Classes are interactive, easy to join, offered day and night and free
                  for our community
                </Text>
                <div className={`${styles.logo} mb-xxs mb-sm-gte-md`}>
                  <img src={props.partner.logo} data-testid="logo" loading="lazy" />
                </div>
              </div>
            </Stack>
          </div>
          <Button
            label="Already a Member? Login Here"
            className={`${styles.ctaButton} mt-4 mb-4`}
            size="sm"
            component="link"
            theme="accentLink"
            href={config.publicRuntimeConfig.auth.loginUrl}
          />
        </Stack>
      ) : (
        <Stack x className={`${coreLayoutContainer} ${styles.root} !mt-8`} spaceBetweenX="lg">
          <Stack y spaceBetweenY="md" className={styles.ctaBlock}>
            <Text
              weight="lg"
              size="lg"
              responsiveSize={{
                lg: 'xl',
              }}
            >
              Stay Active, Engaged, and Connected with Free Classes from GetSetUp!
            </Text>
            <Text tag="p" size="xs" weight="sm">
              Join over millions of older adults learning something new every day and live your best life
            </Text>
            {props.navigationActions && props.navigationActions.length ? (
              <div className="md:mt-14">
                {props.navigationActions.map((action, index) => (
                  <Button
                    label={action.label}
                    className={`${styles.ctaButton} mb-4 md:mb-0 desktop:mt-14`}
                    size={isMobile ? 'sm' : 'md'}
                    component="link"
                    theme="accentLink"
                    href={action.url}
                    key={index}
                  />
                ))}
              </div>
            ) : null}
          </Stack>
          {!isMobile && props.video && (
            <div className={styles.videoContainer}>
              <div>
                <video
                  poster={props.video.posterUrl}
                  data-testid="video"
                  className="absolute top-0 w-full h-full rounded-[var(--border-radius)]"
                  controls
                >
                  <source src={`${props.video.srcUrl}#t=0.001`} type="video/mp4" />
                </video>
              </div>
            </div>
          )}
        </Stack>
      )}
    </header>
  )
}

export default HelenInfoBlock
